import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getPlatformClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Sticky amber banner rendered whenever the current session is an active
 * impersonation. Cannot be dismissed by the user — the only way out is to
 * click "Stop impersonation" (or the banner times out when the server
 * expires the session).
 *
 * Source of truth: `GET /api/platform/whoami`. The server also emits an
 * `X-Impersonation` header on every response as defense-in-depth; we don't
 * wire an interceptor here because the whoami query is refetched on window
 * focus (default for TanStack Query), which covers the practical cases
 * without adding another moving part.
 */
export function ImpersonationBanner(): ReactElement | null {
  const whoami = useQuery({
    queryKey: queryKeys.platform.whoami(),
    queryFn: ({ signal }) => getPlatformClient().whoami(signal),
    // 404/403 are expected for org users — don't spam the network.
    retry: false,
    // Silent-fail on 403 (org user). Returning null from queryFn isn't
    // possible; we catch the error inside and return a fake "not
    // impersonating" payload so org users have an idempotent experience.
    throwOnError: false,
    staleTime: 60_000,
  });

  const stop = useMutation({
    mutationFn: () => getPlatformClient().stopImpersonation(),
    onSuccess: (res) => {
      // Hard navigation (full page load) instead of TanStack Router's
      // soft `navigate()`. Soft nav is async — the org-scoped layout
      // stays mounted long enough for every active `useQuery` observer
      // (me, preferences, labels) to refetch ONCE on the now-platform-
      // idle session, which the tenant middleware correctly 403s. Hard
      // nav tears the React tree down synchronously: no observers, no
      // refetches, no spurious 403s. Acceptable cost for a rare action.
      window.location.assign(res.redirectTo);
    },
  });

  // Countdown state for the expires-at ticker (only runs when there's a bound).
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!whoami.data?.impersonating) return null;

  const expiresAt = whoami.data.impersonationExpiresAt
    ? new Date(whoami.data.impersonationExpiresAt).getTime()
    : null;
  const msLeft = expiresAt ? Math.max(0, expiresAt - now) : null;
  const expiring = msLeft !== null && msLeft > 0 && msLeft < 5 * 60_000;
  const expired = msLeft !== null && msLeft <= 0;

  const bgClass = expired
    ? 'bg-red-200 border-red-400 text-red-950'
    : expiring
      ? 'bg-red-100 border-red-300 text-red-900'
      : 'bg-amber-100 border-amber-300 text-amber-900';

  return (
    <div
      role="alert"
      className={`sticky top-0 z-50 flex items-center justify-between gap-4 border-b px-4 py-2 text-sm ${bgClass}`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>
          Impersonating{' '}
          <span className="font-semibold">
            {whoami.data.impersonatedUserName ?? 'unknown user'}
          </span>{' '}
          in{' '}
          <span className="font-semibold">
            {whoami.data.impersonatedOrganizationName ?? 'unknown org'}
          </span>
          {msLeft !== null && (
            <>
              {' '}
              · <span>{expired ? 'Session expired' : `Expires in ${formatRemaining(msLeft)}`}</span>
            </>
          )}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
        className="flex items-center gap-1"
      >
        {stop.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        ) : (
          <X className="h-3 w-3" aria-hidden="true" />
        )}
        {stop.isPending ? 'Stopping…' : 'Stop impersonation'}
      </Button>
    </div>
  );
}

function formatRemaining(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}
